import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useSocket } from '../hooks/useSocket';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';

export interface Notification {
  id: number;
  user_id: number;
  message_id: number | null;
  job_id: number | null;
  sender_id: number | null;
  notification_type: string;
  title: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
  sender_name?: string;
  sender_email?: string;
  job_number?: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  notificationVolume: number;
  setNotificationVolume: (volume: number) => void;
  loadNotifications: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notificationVolume, setNotificationVolumeState] = useState(() => {
    const saved = localStorage.getItem('notificationVolume');
    return saved !== null ? Number(saved) : 0.5;
  });
  const { socket, isConnected } = useSocket();

  const setNotificationVolume = useCallback((volume: number) => {
    const clamped = Math.max(0, Math.min(1, volume));
    setNotificationVolumeState(clamped);
    localStorage.setItem('notificationVolume', String(clamped));
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/notifications?limit=100', { method: 'GET' });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await apiRequest('/notifications/unread-count', { method: 'GET' });
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
      if (wasUnread) {
        setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      await apiRequest(`/notifications/${notificationId}/read`, { method: 'PUT' });
      await refreshUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n));
      await refreshUnreadCount();
    }
  }, [notifications, refreshUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      await apiRequest('/notifications/read-all', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      await apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      await refreshUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [refreshUnreadCount]);

  const deleteAllNotifications = useCallback(async () => {
    try {
      await apiRequest('/notifications', { method: 'DELETE' });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  }, []);

  const playNotificationSound = useCallback(() => {
    if (notificationVolume === 0) return;
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      if (audioContext.state === 'suspended') audioContext.resume();
      const now = audioContext.currentTime;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      const duration = 0.15;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(notificationVolume * 0.4, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);
      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch {
      // silently ignore audio failures
    }
  }, [notificationVolume]);

  // Single socket listener — only one exists for the entire app
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (notification: Notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      playNotificationSound();
      toast.info(notification.message, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    };

    socket.on('new-notification', handleNewNotification);
    return () => { socket.off('new-notification', handleNewNotification); };
  }, [socket, isConnected, playNotificationSound]);

  useEffect(() => {
    loadNotifications();
    refreshUnreadCount();
  }, [loadNotifications, refreshUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      notificationVolume,
      setNotificationVolume,
      loadNotifications,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
      refreshUnreadCount,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotificationContext must be used within NotificationProvider');
  return ctx;
}
